import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchen } from "../../../redux/actions/kitchenActions";
import { toast } from "react-hot-toast";
import CameraCapture from "../../../components/shared/CameraCapture";
import { useLanguage } from "../../../context/LanguageContext";

import {
  ArrowLeft,
  Upload,
  Save,
  Camera,
  CheckCircle,
  AlertCircle,
  Home,
  Shield,
  Wind,
  Droplets,
  Flame,
  Sparkles,
  Package,
} from "lucide-react";

const SectionDKitchenDining = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  // Helper functions
  const yesNoToNumber = (value) => {
    if (value === t.global.yes) return 1;
    if (value === t.global.no) return 2;
    return null;
  };

  const ratingToNumber = (value) => {
    if (value === t.global.rating_good) return 1;
    if (value === t.global.rating_satisfactory) return 2;
    if (value === t.global.rating_poor) return 3;
    return null;
  };

  // Initialize form state with saved data
  const [form, setForm] = useState({
    d1: "",
    d2: "",
    d3: "",
    d3Photo: null,
    d4: "",
    d5: "",
    d5Document: null,
    d6: "",
    d6Document: null,
    d7: "",
    d8: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [previewImages, setPreviewImages] = useState({});
  const [showCamera, setShowCamera] = useState(false);
  const [cameraField, setCameraField] = useState(null);

  // Load saved draft if exists
  useEffect(() => {
    const loadSavedData = () => {
      try {
        const sectionData = localStorage.getItem("survey_section_d");
        if (sectionData) {
          const savedData = JSON.parse(sectionData);
          const { timestamp, canteenInfo, ...formData } = savedData;
          setForm(formData);
        }
      } catch (err) {
        console.error("Error loading saved data:", err);
      }
    };

    loadSavedData();
  }, []);

  // Auto-save to localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      if (Object.values(form).some((v) => v !== "" && v !== null)) {
        localStorage.setItem("survey_section_d", JSON.stringify(form));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [form]);

  const handleChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];

      // Validate file size immediately
      if (file && file.size > 5 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_size,
        }));
        return;
      }

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (file && !allowedTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          [name]: t.global.err_file_type_long,
        }));
        return;
      }

      setForm((prev) => ({ ...prev, [name]: file }));
      if (file && file.type.startsWith("image/")) {
        const previewUrl = URL.createObjectURL(file);

        setPreviewImages((prev) => ({
          ...prev,
          [name]: previewUrl,
        }));
      } else {
        setPreviewImages((prev) => ({
          ...prev,
          [name]: null,
        }));
      }

      // Clear file error if validation passes
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: undefined }));
      }
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }

    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Required field validations for all radio buttons
    if (!form.d1) newErrors.d1 = t.global.err_a1;
    if (!form.d2) newErrors.d2 = t.global.err_a1;
    if (!form.d3) newErrors.d3 = t.global.err_a1;
    if (!form.d4) newErrors.d4 = t.global.err_a1;
    if (!form.d5) newErrors.d5 = t.global.err_a1;
    if (!form.d6) newErrors.d6 = t.global.err_a1;
    if (!form.d7) newErrors.d7 = t.global.err_a1;
    if (!form.d8) newErrors.d8 = t.global.err_a1;

    // File validation only if file IS uploaded
    if (form.d3Photo && form.d3Photo.size > 5 * 1024 * 1024) {
      newErrors.d3Photo = t.global.err_file_size;
    }
    if (form.d3Photo) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.d3Photo.type)) {
        newErrors.d3Photo = t.global.err_file_type_long;
      }
    }

    if (form.d5Document && form.d5Document.size > 5 * 1024 * 1024) {
      newErrors.d5Document = t.global.err_file_size;
    }
    if (form.d5Document) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.d5Document.type)) {
        newErrors.d5Document = t.global.err_file_type_long;
      }
    }

    if (form.d6Document && form.d6Document.size > 5 * 1024 * 1024) {
      newErrors.d6Document = t.global.err_file_size;
    }
    if (form.d6Document) {
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
      ];
      if (!allowedTypes.includes(form.d6Document.type)) {
        newErrors.d6Document = t.global.err_file_type_long;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /* ================================
     SAVE TO LOCAL STORAGE
  ================================ */
  const handleSave = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    const isPartial =
      !form.d1 ||
      !form.d2 ||
      !form.d3 ||
      !form.d4 ||
      !form.d5 ||
      !form.d6 ||
      !form.d7 ||
      !form.d8;

    try {
      const fd = new FormData();

      /* LOCATION DATA */
      fd.append("district_id", state.district_id);
      fd.append("municipality_id", state.municipality_id);
      fd.append("ward_id", state.ward_id);
      fd.append("area_id", state.area_id);
      fd.append("canteen_type_id", state.canteen_type_id);
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);

      /* SECTION D */
      fd.append("kitch_spce_D1", yesNoToNumber(form.d1));
      fd.append("kitch_spce_D2", yesNoToNumber(form.d2));
      fd.append("kitch_spce_D3", yesNoToNumber(form.d3));

      if (form.d3Photo) {
        fd.append("kitch_spce_D3_upload", form.d3Photo);
      }

      fd.append("kitch_spce_D4", yesNoToNumber(form.d4));
      fd.append("kitch_spce_D5", yesNoToNumber(form.d5));

      if (form.d5Document) {
        fd.append("kitch_spce_D5_upload", form.d5Document);
      }

      fd.append("kitch_spce_D6", yesNoToNumber(form.d6));

      if (form.d6Document) {
        fd.append("kitch_spce_D6_upload", form.d6Document);
      }

      fd.append("kitch_spce_D7", yesNoToNumber(form.d7));
      fd.append("kitch_spce_D8", ratingToNumber(form.d8));

      fd.append("current_section", "kitchenSpace");

      await dispatch(createKitchen(fd));

      // ✅ Save locally after successful API
      const sectionData = {
        ...form,
        isPartial,
        submittedAt: new Date().toISOString(),
      };

      localStorage.setItem("survey_section_d", JSON.stringify(sectionData));

      const completedSections = JSON.parse(
        localStorage.getItem("completedSections") || "{}",
      );
      completedSections.d = true;
      localStorage.setItem(
        "completedSections",
        JSON.stringify(completedSections),
      );

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        navigate("/survey/sections", { state });
      }, 2000);
    } catch (error) {
      console.error("Submission error:", error);

      // ✅ Save locally for offline
      const sectionData = {
        ...form,
        isPartial,
        submittedAt: new Date().toISOString(),
        savedLocally: true,
      };

      localStorage.setItem("survey_section_d", JSON.stringify(sectionData));

      toast.error(t.global.alert_saved_locally);
      setIsSubmitting(false);
      navigate("/survey/sections", { state });
    }
  };

  const ratingOptions = [t.global.rating_good, t.global.rating_satisfactory, t.global.rating_poor];

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        {/* Success Message Overlay */}
        {showSuccess && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-8 max-w-md w-full text-center animate-scale-in">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                {t.global.success_title}
              </h3>
              <p className="text-gray-600 mb-6">
                Section D submitted successfully.
              </p>
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-gray-600">
                  {t.global.redirecting}
                </span>
              </div>
              <button
                onClick={() => {
                  setShowSuccess(false);
                  navigate("/survey/sections", { state });
                }}
                className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                {t.global.continue_now}
              </button>
            </div>
          </div>
        )}

        <div className="bg-white rounded-xl shadow p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">
                {t.modules.kitchen.sections.D.title}
              </h2>
              <p className="text-gray-600">
                {t.modules.kitchen.sections.D.subtitle}
              </p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className="text-sm bg-sky-100 text-sky-800 px-3 py-1 rounded-full flex items-center">
                <Home size={14} className="mr-1" />
                Kitchen Survey
              </div>
              <div className="text-sm bg-green-100 text-green-800 px-3 py-1 rounded-full">
                {state?.area_name ? `${t.global.canteen_name_label} ${state.area_name}` : t.global.na}
              </div>
              {state?.canteenId && (
                <div className="text-sm bg-purple-100 text-purple-800 px-3 py-1 rounded-full">
                  {t.global.canteen_id_label} {state.canteenId}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-6">
            {/* D.1 */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Shield size={18} className="mr-2" />
                D.1 {t.modules.kitchen.sections.D.questions.coveredKitchen}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <p className="text-sm text-gray-600 mb-4">
                {t.modules.kitchen.sections.D.hints.coveredKitchen}
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d1 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d1 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d1"
                      value={option}
                      checked={form.d1 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${form.d1 === option ? "text-blue-700" : "text-gray-700"}`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d1 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d1}
                </p>
              )}
            </div>

            {/* D.2 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Home size={18} className="mr-2" />
                D.2 {t.modules.kitchen.sections.D.questions.slabs}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d2 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d2 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d2"
                      value={option}
                      checked={form.d2 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${form.d2 === option ? "text-blue-700" : "text-gray-700"}`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d2 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d2}
                </p>
              )}
            </div>

            {/* D.3 */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Droplets size={18} className="mr-2" />
                D.3 {t.modules.kitchen.sections.D.questions.sink}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  {t.modules.kitchen.sections.D.hints.sink.split(":")[0]}:
                </p>
                <ul className="text-sm text-amber-700 list-disc pl-5 space-y-1">
                  <li>Running water</li>
                  <li>Soap for handwash</li>
                  <li>Proper tap</li>
                  <li>Proper outlet pipe</li>
                  <li>Connected to drainage system</li>
                </ul>
                <p className="text-sm text-amber-800 mt-2">
                  [If all above conditions are available, respond YES]
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d3 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d3 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d3"
                      value={option}
                      checked={form.d3 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${form.d3 === option ? "text-blue-700" : "text-gray-700"}`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d3 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d3}
                </p>
              )}

              {form.d3 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  <label className="block text-sm font-medium mb-3 text-gray-700">
                    {t.global.uploadPhoto} (Optional):
                  </label>

                  {/* CAMERA + UPLOAD BUTTONS */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("d3Photo");
                        setShowCamera(true);
                      }}
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Camera size={18} /> {t.global.useCamera}
                    </button>

                    <label
                      htmlFor="d3Photo"
                      className={`flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 cursor-pointer font-medium ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload size={18} /> {t.global.uploadPhoto}
                    </label>
                  </div>

                  <input
                    type="file"
                    name="d3Photo"
                    id="d3Photo"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />

                  {/* PREVIEW */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.d3Photo
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.d3Photo ? (
                      <div className="space-y-3">
                        {previewImages.d3Photo && (
                          <img
                            src={previewImages.d3Photo}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg shadow"
                          />
                        )}

                        <div className="flex items-center justify-between">
                          <p className="text-gray-700 font-medium text-sm break-all">
                            {form.d3Photo.name}
                          </p>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, d3Photo: null }));
                              setPreviewImages((prev) => ({
                                ...prev,
                                d3Photo: null,
                              }));
                            }}
                            className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        {t.global.captureHint} sink photo
                      </p>
                    )}
                  </div>

                  {errors.d3Photo && (
                    <p className="mt-2 text-sm text-red-600">
                      {errors.d3Photo}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* D.4 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Wind size={18} className="mr-2" />
                D.4 {t.modules.kitchen.sections.D.questions.ventilation}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d4 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d4 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d4"
                      value={option}
                      checked={form.d4 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${form.d4 === option ? "text-blue-700" : "text-gray-700"}`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d4 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d4}
                </p>
              )}
            </div>

            {/* D.5 */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Sparkles size={18} className="mr-2" />
                D.5 {t.modules.kitchen.sections.D.questions.deepCleaning}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  {t.modules.kitchen.sections.D.hints.deepCleaning.split(":")[0]}:
                </p>
                <ul className="text-sm text-amber-700 list-disc pl-5 space-y-1">
                  <li>Kitchen wall tiles/slabs</li>
                  <li>Ceiling</li>
                  <li>Cobweb removal</li>
                  <li>Doors and window cleaning</li>
                  <li>Furniture cleaning</li>
                </ul>
                <p className="text-sm text-amber-800 mt-2">
                  [If record maintained and updated daily, assign YES]
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d5 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d5 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d5"
                      value={option}
                      checked={form.d5 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${form.d5 === option ? "text-blue-700" : "text-gray-700"}`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d5 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d5}
                </p>
              )}

              {form.d5 === t.global.yes && (
                <div className="mt-6 p-4 bg-green-50 border-2 border-dashed border-green-300 rounded-lg space-y-5">
                  <label className="block text-sm font-medium mb-3 text-gray-700">
                    Upload deep cleaning record (Optional):
                  </label>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("d5Document");
                        setShowCamera(true);
                      }}
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Camera size={18} /> {t.global.useCamera}
                    </button>

                    <label
                      htmlFor="d5Document"
                      className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Upload size={18} /> {t.global.uploadPhoto}
                    </label>
                  </div>

                  <input
                    type="file"
                    name="d5Document"
                    id="d5Document"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />

                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.d5Document
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.d5Document ? (
                      <div className="space-y-3">
                        {previewImages.d5Document && (
                          <img
                            src={previewImages.d5Document}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg shadow"
                          />
                        )}

                        <div className="flex items-center justify-between">
                          <p className="text-gray-700 font-medium text-sm break-all">
                            {form.d5Document.name}
                          </p>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({
                                ...prev,
                                d5Document: null,
                              }));
                              setPreviewImages((prev) => ({
                                ...prev,
                                d5Document: null,
                              }));
                            }}
                            className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        {t.global.captureHint} deep cleaning record
                      </p>
                    )}
                  </div>

                  {errors.d5Document && (
                    <p className="mt-2 text-sm text-red-600">
                      {errors.d5Document}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* D.6 */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Package size={18} className="mr-2" />
                D.6 {t.modules.kitchen.sections.D.questions.storeArea}
                <span className="text-red-500 ml-1">*</span>
              </label>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800 font-medium mb-1">
                  {t.modules.kitchen.sections.D.hints.storeArea.split(":")[0]}:
                </p>
                <ul className="text-sm text-amber-700 list-disc pl-5 space-y-1">
                  <li>Deep cleaning of store room needed every week</li>
                  <li>Housekeeping checklist maintained and updated daily</li>
                </ul>
                <p className="text-sm text-amber-800 mt-2">
                  [If all above conditions are met, assign YES]
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d6 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d6 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d6"
                      value={option}
                      checked={form.d6 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${form.d6 === option ? "text-blue-700" : "text-gray-700"}`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d6 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d6}
                </p>
              )}

              {form.d6 === t.global.yes && (
                <div className="mt-4">
                  {/* CAMERA + UPLOAD BUTTONS */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <button
                      type="button"
                      onClick={() => {
                        setCameraField("d6Document");
                        setShowCamera(true);
                      }}
                      disabled={isSubmitting}
                      className="flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 font-medium disabled:opacity-50"
                    >
                      <Camera size={18} /> {t.global.useCamera}
                    </button>

                    <label
                      htmlFor="d6Document"
                      className={`flex items-center justify-center gap-2 p-3 border border-green-300 bg-green-900/10 text-green-900 rounded-lg active:scale-95 active:bg-green-700 active:text-white transition-all duration-150 cursor-pointer font-medium ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      <Upload size={18} /> {t.global.uploadPhoto}
                    </label>
                  </div>

                  <input
                    type="file"
                    name="d6Document"
                    id="d6Document"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleChange}
                    className="hidden"
                    disabled={isSubmitting}
                  />

                  {/* PREVIEW BOX */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center transition ${
                      form.d6Document
                        ? "border-yellow-400 bg-yellow-50"
                        : "border-gray-300 bg-gray-50"
                    }`}
                  >
                    {form.d6Document ? (
                      <div className="space-y-3">
                        {/* IMAGE PREVIEW (only if image) */}
                        {previewImages.d6Document && (
                          <img
                            src={previewImages.d6Document}
                            alt="Preview"
                            className="max-h-48 mx-auto rounded-lg shadow"
                          />
                        )}

                        <div className="flex items-center justify-between">
                          <div className="text-left">
                            <p className="text-gray-700 font-medium text-sm break-all">
                              {form.d6Document.name}
                            </p>
                          </div>

                          <button
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({
                                ...prev,
                                d6Document: null,
                              }));
                              setPreviewImages((prev) => ({
                                ...prev,
                                d6Document: null,
                              }));
                            }}
                            className="ml-4 text-red-600 px-3 py-1 rounded-md hover:bg-red-600 hover:text-white transition text-xs font-bold"
                          >
                            {t.global.remove}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic text-sm">
                        {t.global.captureHint} store room maintenance record
                      </p>
                    )}
                  </div>

                  {errors.d6Document && (
                    <p className="mt-2 text-sm text-red-600">
                      {errors.d6Document}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* D.7 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3 flex items-center">
                <Flame size={18} className="mr-2" />
                D.7 {t.modules.kitchen.sections.D.questions.fuelType}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[t.global.yes, t.global.no].map((option) => (
                  <label
                    key={option}
                    className={`flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                      form.d7 === option
                        ? "bg-blue-100 border-blue-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d7 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d7"
                      value={option}
                      checked={form.d7 === option}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <div className="flex items-center">
                      <span
                        className={`text-lg font-medium ${form.d7 === option ? "text-blue-700" : "text-gray-700"}`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d7 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d7}
                </p>
              )}
            </div>

            {/* D.8 */}
            <div className="border rounded-lg p-4 bg-blue-50 border-blue-200">
              <label className="block font-medium mb-3 flex items-center">
                <Sparkles size={18} className="mr-2" />
                D.8 {t.modules.kitchen.sections.D.questions.overallCleanliness}
                <span className="text-red-500 ml-1">*</span>
              </label>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {ratingOptions.map((option) => (
                  <label
                    key={option}
                    className={`flex flex-col items-center p-5 border-2 rounded-xl cursor-pointer transition-all ${
                      form.d8 === option
                        ? option === t.global.rating_good
                          ? "bg-green-50 border-green-400 shadow-sm"
                          : option === t.global.rating_satisfactory
                            ? "bg-amber-50 border-amber-400 shadow-sm"
                            : "bg-red-50 border-red-400 shadow-sm"
                        : "bg-white border-gray-200 hover:border-gray-300"
                    } ${errors.d8 ? "border-red-300" : ""}`}
                  >
                    <input
                      type="radio"
                      name="d8"
                      value={option}
                      checked={form.d8 === option}
                      onChange={handleChange}
                      className="mb-3 h-5 w-5"
                      required
                    />
                    <div className="text-center">
                      <div
                        className={`text-2xl mb-1 ${
                          form.d8 === option
                            ? option === t.global.rating_good
                              ? "text-green-600"
                              : option === t.global.rating_satisfactory
                                ? "text-amber-600"
                                : "text-red-600"
                            : "text-gray-400"
                        }`}
                      >
                        {option === t.global.rating_good
                          ? "⭐"
                          : option === t.global.rating_satisfactory
                            ? "⭐"
                            : "⭐"}
                      </div>
                      <span
                        className={`text-lg font-medium ${
                          form.d8 === option
                            ? option === t.global.rating_good
                              ? "text-green-800"
                              : option === t.global.rating_satisfactory
                                ? "text-amber-800"
                                : "text-red-800"
                            : "text-gray-700"
                        }`}
                      >
                        {option}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
              {errors.d8 && (
                <p className="mt-2 text-sm text-red-600 flex items-center">
                  <AlertCircle size={14} className="mr-1" /> {errors.d8}
                </p>
              )}
            </div>

            {/* Optional Fields Notice */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-5 w-5 text-amber-500" />
                </div>
                <div className="ml-3">
                  <p className="text-sm text-amber-800">
                    <span className="font-medium">Note:</span> Photo/document
                    uploads are <span className="font-medium">optional</span>.
                    However, if you answer "YES" to questions requiring uploads
                    (D.3, D.5, D.6) and don't provide them, this section will be
                    marked as{" "}
                    <span className="font-medium">"Partially Filled"</span>.
                  </p>
                </div>
              </div>
            </div>

            {/* Footer Info */}
            <div className="pt-4 border-t border-gray-200">
              <div className="text-center text-sm text-gray-500">
                <span className="text-red-500 font-medium">
                  * Required fields must be completed
                </span>
                <p className="text-sm text-gray-600 mt-1">
                  All other information is optional but recommended for better
                  assessment.
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                type="button"
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 rounded-lg hover:bg-gray-300 transition font-medium flex items-center"
                disabled={isSubmitting}
              >
                <ArrowLeft size={18} className="mr-2" />
                {t.global.back_to_sections}
              </button>

              <button
                type="button"
                onClick={handleSave}
                disabled={isSubmitting}
                className={`px-6 py-3 rounded-lg font-medium flex items-center transition ${
                  isSubmitting
                    ? "bg-blue-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    {t.global.submitting}
                  </>
                ) : (
                  <>
                    <Save size={18} className="mr-2" />
                    {t.global.submit_section_d}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom Animation */}
      <style jsx>{`
        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
      {showCamera && (
        <CameraCapture
          onCapture={(file) => {
            setForm((prev) => ({
              ...prev,
              [cameraField]: file,
            }));

            if (file.type.startsWith("image/")) {
              const previewUrl = URL.createObjectURL(file);
              setPreviewImages((prev) => ({
                ...prev,
                [cameraField]: previewUrl,
              }));
            }

            setShowCamera(false);
          }}
          onClose={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

export default SectionDKitchenDining;